/**
 * Хуки данных модуля «Отзывы» — лента + модерация (скрытие) менеджеру.
 *
 * Бэк: internal/reviews —
 *   GET  /v1/reviews?state=&target_type=&limit=&offset= → { items }
 *   POST /v1/admin/reviews/{id}/hide  body { reason } → скрыть (модерация)
 * RBAC: чтение — authenticated (бэк фильтрует по тенанту/роли); hide —
 * модератор/менеджер (reviews moderator capability).
 *
 * Состояния отзыва: published / hidden / deleted.
 * Server-state только через React Query; hide инвалидирует список.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetch } from '@hsc/api-client';

/** Ответ автора (отель/вендор) на отзыв. */
export interface ReviewResponse {
  id: string;
  text: string;
  author_display?: string;
  responded_at: string;
  edited_at?: string;
}

/** Проекция отзыва (см. internal/reviews/.../dto.reviewResponse). */
export interface Review {
  id: string;
  tenant_id?: string;
  target_type: string;
  target_id: string;
  author_user_id: string;
  rating: number;
  text: string;
  language?: string;
  state: string;
  created_at: string;
  hidden_at?: string;
  deleted_at?: string;
  response?: ReviewResponse;
}

export interface ReviewsListResponse {
  items: Review[];
}

export interface ReviewsFilters {
  state?: string;
  target_type?: string;
  limit?: number;
}

const REVIEWS_KEY = ['reviews', 'list'] as const;

export function useReviews(filters?: ReviewsFilters) {
  return useQuery<ReviewsListResponse>({
    queryKey: [...REVIEWS_KEY, filters ?? {}],
    queryFn: async () => {
      const params: Record<string, string | number> = {};
      if (filters?.state) params['state'] = filters.state;
      if (filters?.target_type) params['target_type'] = filters.target_type;
      if (filters?.limit !== undefined) params['limit'] = filters.limit;
      const raw = await customFetch<{ items?: Review[] }>(
        Object.keys(params).length > 0
          ? { url: '/reviews', method: 'GET', params }
          : { url: '/reviews', method: 'GET' },
      );
      return { items: raw.items ?? [] };
    },
    staleTime: 30_000,
  });
}

/** Скрыть отзыв (модерация) — POST /v1/admin/reviews/{id}/hide. */
export function useHideReview() {
  const qc = useQueryClient();
  return useMutation<Review, Error, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) =>
      customFetch<Review>({
        url: `/admin/reviews/${encodeURIComponent(id)}/hide`,
        method: 'POST',
        data: { reason },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['reviews'] }),
  });
}

/** Тело создания отзыва (POST /v1/reviews) — гость оставляет отзыв. */
export interface CreateReviewInput {
  target_type: string;
  target_id: string;
  rating: number;
  text?: string;
  /** Источник: проживание (для anti-spam «куплено/прожито» на бэке). */
  source_stay_id?: string;
}

/**
 * Оставить отзыв (гость) — POST /v1/reviews. На бэке отзыв уходит на модерацию
 * (24 ч) перед публикацией. Инвалидирует ленту своих отзывов.
 */
export function useCreateReview() {
  const qc = useQueryClient();
  return useMutation<Review, Error, CreateReviewInput>({
    mutationFn: (input) =>
      customFetch<Review>({ url: '/reviews', method: 'POST', data: input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['reviews'] }),
  });
}
